import { lazy } from "react";

/**
 * Detect the family of errors thrown when a dynamically-imported chunk can no
 * longer be fetched. This happens after a new deploy replaces the hashed chunk
 * filenames the currently-loaded index.html points at: the browser requests an
 * old chunk URL and gets the SPA's index.html (HTML) back instead of JS, which
 * surfaces as a ChunkLoadError and/or "Unexpected token '<'".
 */
export const isChunkLoadError = (error) => {
  if (!error) return false;
  const name = error.name || "";
  const message = error.message || "";
  return (
    name === "ChunkLoadError" ||
    /Loading chunk [\w-]+ failed/i.test(message) ||
    /Loading CSS chunk/i.test(message) ||
    /Failed to fetch dynamically imported module/i.test(message) ||
    // Vite's wording when a lazy chunk's stylesheet is gone after a redeploy.
    /Unable to preload CSS/i.test(message) ||
    /'<'|Unexpected token '<'|importing a module script failed/i.test(message)
  );
};

/**
 * Drop-in replacement for React.lazy that survives stale deploys. When a chunk
 * fails to load (almost always because the user is on an old index.html after a
 * redeploy), force a one-time full page reload so the browser fetches the fresh
 * index.html and the current chunk filenames. A sessionStorage flag prevents a
 * reload loop if the chunk is genuinely missing for some other reason.
 */
export default function lazyWithRetry(importFn) {
  return lazy(async () => {
    const reloadKey = "chunk-reload-attempted";
    try {
      const component = await importFn();
      // Successful load: clear the guard so a future real failure can reload.
      window.sessionStorage.removeItem(reloadKey);
      return component;
    } catch (error) {
      if (isChunkLoadError(error)) {
        const alreadyReloaded =
          window.sessionStorage.getItem(reloadKey) === "true";
        if (!alreadyReloaded) {
          window.sessionStorage.setItem(reloadKey, "true");
          // Force a real network fetch of the latest index.html.
          window.location.reload();
          // Return a never-resolving promise so React keeps the Suspense
          // fallback up while the reload happens.
          return new Promise(() => {});
        }
      }
      throw error;
    }
  });
}
